import { forwardRef, Fragment, InputHTMLAttributes, ReactElement, RefObject, useRef } from 'react';

import styled from 'styled-components';

import { useAriaId, useValue } from '@components/component-helpers';
import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { IconFormIndicatorLocked } from '@components/icons/form-indicator-locked';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { VisuallyHidden } from '@components/visually-hidden';
import { Color, theme } from '@styles/theme';

type DisallowedProps = 'onChange' | 'disabled' | 'defaultValue';
type BaseTextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, DisallowedProps>;

export type TextFieldProps = BaseTextFieldProps & {
  label: string;
  hideLabel?: boolean;
  labelIcon?: ReactElement<IconProps>;
  iconLeft?: ReactElement<IconProps>;
  iconRight?: ReactElement<IconProps>;
  onChangeDebounce?: number;
  onChange?(newValue: string): void;
  error?: string;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  defaultValue?: string;
  dataTestid?: string;
  dataCy?: string;
  helpText?: string;
  stopPropagation?: boolean;
  hideDisabledIndicator?: boolean;
  maxChars?: number;
  labelColor?: Color;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
  let {
    iconLeft,
    iconRight,
    onChangeDebounce = 0,
    onChange: suppliedOnChange = () => null,
    error = undefined,
    label,
    labelIcon,
    hideLabel = false,
    isDisabled = false,
    isFullWidth = false,
    type: inputType = 'text',
    defaultValue = '',
    helpText = '',
    value: suppliedValue,
    dataTestid,
    dataCy,
    stopPropagation = false,
    hideDisabledIndicator = false,
    maxChars,
    labelColor = 'textPrimary',
    id,
    onBlur,
    ...rest
  } = props;

  const inputId = useAriaId('input');
  const inputRef = useRef<HTMLInputElement>(null);
  const actualRef = (ref || inputRef) as RefObject<HTMLInputElement>;
  const actualId = id ?? inputId;

  const isControlled = suppliedValue !== undefined;

  const { onChange: localOnChange, value: localValue } = useValue({
    defaultValue,
    onChange: suppliedOnChange,
    onChangeDebounce: isControlled ? 0 : onChangeDebounce,
  });

  const onChange = isControlled ? suppliedOnChange : localOnChange;
  const value = isControlled ? suppliedValue : localValue;

  const FormLabelContainer = hideLabel ? VisuallyHidden : Fragment;

  return (
    <CustomStack space={1} data-full-width={isFullWidth}>
      <FormLabelContainer>
        <FormLabel htmlFor={actualId} text={label} icon={labelIcon} color={labelColor} />
      </FormLabelContainer>
      <Stack space={1} orientation="vertical" data-full-width={isFullWidth}>
        <InputContainer
          className="input-container"
          data-error={Boolean(error)}
          data-disabled={isDisabled}
          onClick={() => actualRef.current?.focus()}
        >
          {iconLeft}
          <input
            maxLength={maxChars}
            disabled={isDisabled}
            spellCheck={false}
            readOnly={isDisabled}
            id={actualId}
            ref={actualRef}
            type={inputType}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => {
              if (!actualRef.current) return;
              actualRef.current.value = e.target.value.trim();
              onChange(e.target.value.trim());
              onBlur?.(e);
            }}
            data-testid={dataTestid}
            data-cy={dataCy}
            onKeyDown={(e) => {
              if (stopPropagation) {
                e.stopPropagation();
              }
            }}
            {...rest}
          />
          {isDisabled && !hideDisabledIndicator ? <IconFormIndicatorLocked /> : iconRight}
        </InputContainer>
        <FormError text={error} />
        {helpText && <Text font="regular14" color="purple80" text={helpText} />}
      </Stack>
    </CustomStack>
  );
});

const CustomStack = styled(Stack)({
  '&[data-full-width="true"]': {
    flexGrow: '1',
  },
});

export const InputContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 0,
})({
  borderStyle: 'solid',
  height: theme.base.height,
  outline: 'none',
  padding: '0 0.5rem',
  transitionDuration: '150ms',
  transitionProperty: 'color, background, border',
  transitionTimingFunction: 'ease-out',

  borderWidth: theme.form.border.width,
  borderRadius: theme.form.border.radius,
  borderColor: theme.form.border.color,
  backgroundColor: theme.form.bg.color,

  svg: {
    transition: 'color 150ms ease-out',
    color: `var(--icon-color, ${theme.form.icon.color})`,
    flexShrink: 0,
  },

  input: {
    display: 'block',
    height: '100%',
    width: '100%',
    border: 'none',
    outline: 'none',
    flexGrow: 1,
    background: 'transparent',
    font: theme.font.regular16,
    color: theme.form.value.color,
    margin: '0 0.25rem',
    padding: 0,
  },

  'input::placeholder': {
    font: theme.font.regular14,
    color: theme.form.placeholder.color,
  },

  '&[data-disabled="false"]': {
    '&:hover': {
      backgroundColor: theme.form.bg.colorHover,
    },

    '&:focus-within': {
      '&[data-error="false"]': {
        backgroundColor: theme.form.bg.colorFocus,
        borderColor: theme.form.border.colorFocus,
      },
      'input::placeholder': {
        color: theme.form.placeholder.colorFocus,
      },
      input: {
        color: theme.form.value.colorFocus,
      },
      svg: {
        color: theme.form.icon.colorFocus,
      },
    },
  },

  '&[data-error="true"]': {
    borderColor: theme.color.elementRed100,
    color: theme.color.elementRed100,
    background: theme.color.redTrans5,

    '&:hover': {
      background: theme.color.redTrans10,
    },
  },
});
